import { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { IoCloseSharp } from "react-icons/io5";
import { BiSolidError } from "react-icons/bi";
import { RiDeleteBin6Fill } from "react-icons/ri";
import AdminPagination from "../../../component/pagination/AdminPagination";
import SkeletonLoading from "../../../component/SkeletonLoading";
import {
  useGetPaymentHistoryQuery,
  useDeleteSubscriptionHistoryMutation,
} from "../../../store/api/school/PaymentHistorySlice";
import { formatDateToLocal } from "../../../utils/dateAndTimeConversion";

const initialPagination = { limit: 10, page: 1, total: "", totalPages: "" };

const statusClass = (status) => {
  if (status === "Completed") return "status bg-success text-white";
  if (status === "Cancelled") return "status bg-danger text-white";
  return "status unavailable";
};

const PaymentHistory = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.UserReducer);
  const canDelete = user?.role === "school" || user?.role === "vendor";
  const [history, setHistory] = useState([]);
  const [pagination, setPagination] = useState(initialPagination);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const [deleteSubscriptionHistory, { isLoading: isDeleting }] =
    useDeleteSubscriptionHistoryMutation();

  const handleDelete = async () => {
    try {
      await deleteSubscriptionHistory(deleteTargetId).unwrap();
      toast.success("Payment history deleted.");
      setDeleteTargetId(null);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete.");
      setDeleteTargetId(null);
    }
  };

  const { data, isSuccess, isLoading, isFetching } = useGetPaymentHistoryQuery({
    page: pagination.page,
    limit: pagination.limit,
  });

  useEffect(() => {
    if (isSuccess && data?.data) {
      setHistory(data.data.history);
      setPagination((prev) => ({
        ...prev,
        total: data.data.pagination.total,
        page: data.data.pagination.page,
        limit: data.data.pagination.limit,
        totalPages: data.data.pagination.pages,
      }));
    }
  }, [data, isSuccess]);

  const handlePageChange = (page) =>
    setPagination((prev) => ({ ...prev, page }));
  const handleLimitChange = (limit) =>
    setPagination((prev) => ({ ...prev, limit, page: 1 }));

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between">
        <div className="dbSubHd mb-2">
          {t("total")}: {pagination.total}
        </div>
      </div>

      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th>
                {t("Sr")}.{t("no")}.
              </th>
              <th>
                {t("order")} {t("number")}
              </th>
              <th>{t("amount")}</th>
              <th>{t("currency")}</th>
              <th>
                {t("transaction")} {t("id")}
              </th>
              <th>
                {t("payment")} {t("status")}
              </th>
              <th>
                {t("start")} {t("subscription")}
              </th>
              <th>
                {t("end")} {t("subscription")}
              </th>
              <th>{t("plan")}</th>
              {canDelete && <th>{t("action")}</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading MainLength={10} length={canDelete ? 10 : 9} />
            ) : history?.length > 0 ? (
              history.map((item, i) => (
                <tr key={item.id}>
                  <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                  <td className="text-nowrap">{item.order_number}</td>
                  <td className="text-nowrap">
                    {item.currency === "CAD" ? "C$" : "$"}{(item.amount_cents / 100).toFixed(2)}
                  </td>
                  <td className="text-nowrap">{item.currency}</td>
                  <td className="text-nowrap">{item.transaction_id || "—"}</td>
                  <td className="text-nowrap">
                    <span className={statusClass(item.payment_status)}>
                      {item.payment_status}
                    </span>
                  </td>
                  <td className="text-nowrap">
                    {formatDateToLocal(item.start_subscription)}
                  </td>
                  <td className="text-nowrap">
                    {formatDateToLocal(item.end_subscription)}
                  </td>
                  <td className="text-nowrap">{item.plan?.name}</td>
                  {canDelete && (
                    <td className="text-nowrap">
                      {item.payment_status !== "Completed" && (
                        <Button
                          className="rounded-circle"
                          onClick={() => setDeleteTargetId(item.id)}
                          title="Delete"
                        >
                          <RiDeleteBin6Fill />
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr className="text-center">
                <td colSpan={canDelete ? 10 : 9}>
                  {t("no")} {t("details")} {t("found")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      <Modal show={!!deleteTargetId} centered>
        <Button className="closePopup" onClick={() => setDeleteTargetId(null)}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div className="text-center mb-3 errorIcon">
            <BiSolidError />
          </div>
          <div className="text-center mb-4 deleteHd">
            {`${t("are")} ${t("you")} ${t("sure")} ${t("want")} ${t("to")} ${t("delete")} ${t("this")} ${t("payment")} ${t("record")}?`}
          </div>
          <div className="text-center">
            <Button
              className="smallBtn cancel me-3"
              onClick={() => setDeleteTargetId(null)}
            >
              {t("btns.cancel")}
            </Button>
            <Button
              className="smallBtn auto"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                />
              ) : (
                t("btns.delete")
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PaymentHistory;
